//MARCA | Product & Variant Image Preloader for Shopify
// Optimized for stores with many color variants (30+)

class ProductVariantPreloader {
  constructor() {
    this.preloadedUrls = new Set();
    this.preloadedImages = new Set();
    this.preloadDelay = 100; // Reduced delay for better responsiveness
    this.preloadQueue = [];
    this.maxConcurrentPreloads = 3; // Limit concurrent requests
    this.activePreloads = 0;
    this.preloadTimeouts = {};
    this.hoverTimeout = null;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // console.log('MARCA | 🎨 Product & Variant Preloader initialized');
    // if (this.isIOS) {
    //   console.log('MARCA | 📱 iOS device detected - using optimized preloading strategy');
    // }
    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupPreloaders());
    } else {
      this.setupPreloaders();
    }
  }

  setupPreloaders() {
    // console.log('MARCA | 🔧 Setting up preloaders...');
    
    // Setup product card preloading (collection pages)
    this.setupProductCardPreloading();
    
    // Setup variant swatch preloading (both collection and product pages)
    this.setupVariantSwatchPreloading();
    
    // Setup variant picker option preloading (product pages)
    this.setupVariantPickerPreloading();
    
    // Preload on scroll for visible products
    this.setupScrollPreloading();
    
    // For iOS and product pages, preload variant gallery images immediately
    if (this.isIOS || this.isProductPage()) {
      this.setupImmediateVariantPreloading();
    }
    
    // console.log('MARCA | ✅ Preloaders setup complete');
  }

  /**
   * Check if we're on a product page
   */
  isProductPage() {
    return document.querySelector('.product-gallery') !== null || 
           document.querySelector('variant-picker') !== null;
  }

  /**
   * Setup immediate preloading of variant gallery images (for iOS and product pages)
   */
  setupImmediateVariantPreloading() {
    // console.log('MARCA | ⚡ Setting up immediate variant preloading...');
    
    // Find the product gallery
    const productGallery = document.querySelector('.product-gallery');
    if (!productGallery) return;
    
    // Get all media items in the gallery (including hidden ones for other variants)
    const allMediaItems = productGallery.querySelectorAll('.product-gallery__media img');
    // console.log(`MARCA | 🖼️ Found ${allMediaItems.length} gallery images to preload`);
    
    // Preload all gallery images (they're already in the DOM, but may not be loaded)
    allMediaItems.forEach((img, index) => {
      const src = img.getAttribute('src') || img.getAttribute('data-src');
      const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset');
      
      if (srcset) {
        // Parse srcset and preload the larger sizes
        this.preloadFromSrcset(srcset);
      } else if (src) {
        // Preload larger version of single src
        this.preloadLargerImageSizes(src);
      }
      
      // Don't log every single image to avoid console spam
      // if (index < 3) {
      //   console.log(`MARCA | 📸 Preloading gallery image ${index + 1}`);
      // }
    });
    
    // Also look for images referenced in variant data but not yet in DOM
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      this.preloadVariantGalleryImages(variantPicker);
    }
    
    // IMPORTANT: Preload ALL thumbnail swatches immediately (small files, load fast)
    this.preloadAllThumbnailSwatches();
  }

  /**
   * Preload all thumbnail swatch images on the page
   */
  preloadAllThumbnailSwatches() {
    const thumbnailSwatches = document.querySelectorAll('.thumbnail-swatch img, .color-swatch img, .block-swatch img');
    // console.log(`MARCA | 🎨 Preloading ${thumbnailSwatches.length} swatch thumbnails...`);
    
    thumbnailSwatches.forEach((img, index) => {
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      
      if (srcset) {
        this.preloadThumbnailSrcset(srcset);
      } else if (src) {
        this.preloadThumbnailSizes(src);
      }
    });
  }

  /**
   * Preload variant gallery images from variant picker data
   */
  preloadVariantGalleryImages(variantPicker) {
    const variantInputs = variantPicker.querySelectorAll('input[data-variant-media]');
    // console.log(`MARCA | 🎨 Found ${variantInputs.length} variants with media data`);
    
    variantInputs.forEach((input, index) => {
      const variantMedia = input.getAttribute('data-variant-media');
      
      if (variantMedia) {
        try {
          const mediaData = JSON.parse(variantMedia);
          // Preload the full-size gallery image, not just the thumbnail
          this.preloadGalleryImage(mediaData);
        } catch (e) {
          console.warn('MARCA | ⚠️ Failed to parse variant media:', e);
        }
      }
    });
  }

  /**
   * Preload a gallery image (larger sizes for product gallery)
   */
  preloadGalleryImage(mediaData) {
    if (!mediaData || !mediaData.src) return;
    
    // For gallery images, we need larger sizes
    const imageUrls = this.generateGalleryImageUrls(mediaData.src);
    
    imageUrls.forEach(url => {
      if (!this.preloadedImages.has(url)) {
        // Use immediate preloading for gallery images (no delay)
        this.preloadImage(url);
      }
    });
  }

  /**
   * Generate gallery-optimized image URLs (larger sizes)
   */
  generateGalleryImageUrls(baseSrc) {
    const urls = [];
    const cleanSrc = baseSrc.split('?')[0];
    
    // For gallery images, we need larger sizes
    // These match the sizes commonly used in product galleries
    const sizes = [800, 1000, 1200, 1400, 1600, 1800];
    
    sizes.forEach(size => {
      if (cleanSrc.includes('cdn.shopify.com')) {
        const url = `${cleanSrc}?width=${size}`;
        urls.push(url);
      }
    });
    
    return urls.length > 0 ? urls : [baseSrc];
  }

  /**
   * Preload larger image sizes from a source URL
   */
  preloadLargerImageSizes(src) {
    const cleanSrc = src.split('?')[0];
    
    if (cleanSrc.includes('cdn.shopify.com')) {
      // Preload a few key sizes
      const sizes = [1000, 1400, 1800];
      sizes.forEach(size => {
        const url = `${cleanSrc}?width=${size}`;
        if (!this.preloadedImages.has(url)) {
          this.preloadImage(url);
        }
      });
    }
  }

  /**
   * Preload images from srcset attribute (for gallery images - larger sizes)
   */
  preloadFromSrcset(srcset) {
    const sources = srcset.split(',');
    
    // Preload the larger images from the srcset
    sources.slice(-3).forEach(source => {
      const url = source.trim().split(' ')[0];
      if (url && !this.preloadedImages.has(url)) {
        this.preloadImage(url);
      }
    });
  }

  /**
   * Preload thumbnail swatch images from srcset (smaller sizes: 48-136px)
   */
  preloadThumbnailSrcset(srcset) {
    const sources = srcset.split(',');
    
    // Preload ALL thumbnail sizes (they're small, so preload them all)
    sources.forEach(source => {
      const url = source.trim().split(' ')[0];
      if (url && !this.preloadedImages.has(url)) {
        this.preloadImage(url);
      }
    });
  }

  /**
   * Preload thumbnail swatch sizes from a base URL
   */
  preloadThumbnailSizes(src) {
    const cleanSrc = src.split('?')[0];
    
    if (cleanSrc.includes('cdn.shopify.com')) {
      // Preload all thumbnail sizes
      const thumbnailSizes = [48, 68, 96, 136];
      thumbnailSizes.forEach(size => {
        const url = `${cleanSrc}?width=${size}`;
        if (!this.preloadedImages.has(url)) {
          this.preloadImage(url);
        }
      });
    }
  }

  /**
   * Setup preloading for product cards in collections
   */
  setupProductCardPreloading() {
    const productCards = document.querySelectorAll('.product-card');
    // console.log(`MARCA | 📦 Found ${productCards.length} product cards`);

    productCards.forEach((card, index) => {
      const productLink = card.querySelector('a[data-instant], a.product-card__media');
      
      if (!productLink) return;
      
      const productUrl = productLink.href;
      const productHandle = card.getAttribute('handle');
      
      // Add hover listener to preload product page
      productLink.addEventListener('mouseenter', () => {
        this.schedulePagePreload(productUrl);
        
        // Also try to preload variant images if product handle is available
        if (productHandle) {
          this.preloadProductVariantImages(productHandle, productLink);
        }
      }, { passive: true });
      
      // Focus event for accessibility
      productLink.addEventListener('focus', () => {
        this.schedulePagePreload(productUrl);
      }, { passive: true });
    });
  }

  /**
   * Setup preloading for variant swatches on product cards and product pages
   */
  setupVariantSwatchPreloading() {
    // Product card swatches
    const swatchInputs = document.querySelectorAll('.product-card input[data-variant-media], .color-swatch + input[data-variant-media], .block-swatch + input[data-variant-media], .thumbnail-swatch + input[data-variant-media]');
    // console.log(`MARCA | 🎨 Found ${swatchInputs.length} variant swatches with media`);

    swatchInputs.forEach(input => {
      const label = input.previousElementSibling || input.nextElementSibling;
      if (!label) return;

      // Get variant media data
      const variantMedia = input.getAttribute('data-variant-media');
      const variantSecondaryMedia = input.getAttribute('data-variant-secondary-media');
      
      const preloadSwatchImages = () => {
        if (variantMedia) {
          try {
            const mediaData = JSON.parse(variantMedia);
            // On product pages, preload gallery images; on collection pages, preload card images
            if (this.isProductPage()) {
              this.preloadGalleryImage(mediaData);
            } else {
              this.preloadVariantImage(mediaData);
            }
          } catch (e) {
            console.warn('MARCA | ⚠️ Failed to parse variant media:', e);
          }
        }
        
        if (variantSecondaryMedia) {
          try {
            const secondaryMediaData = JSON.parse(variantSecondaryMedia);
            if (this.isProductPage()) {
              this.preloadGalleryImage(secondaryMediaData);
            } else {
              this.preloadVariantImage(secondaryMediaData);
            }
          } catch (e) {
            console.warn('MARCA | ⚠️ Failed to parse secondary variant media:', e);
          }
        }
      };

      label.addEventListener('mouseenter', preloadSwatchImages, { passive: true });
      label.addEventListener('focus', preloadSwatchImages, { passive: true });
    });
  }

  /**
   * Setup preloading for variant picker options on product pages
   */
  setupVariantPickerPreloading() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    // console.log('MARCA | 🎯 Setting up variant picker preloading');

    // Find all variant option inputs
    const variantInputs = variantPicker.querySelectorAll('input[data-variant-media]');
    
    variantInputs.forEach(input => {
      const label = input.previousElementSibling || input.nextElementSibling;
      if (!label) return;

      const preloadVariantMedia = () => {
        const variantMedia = input.getAttribute('data-variant-media');
        const variantSecondaryMedia = input.getAttribute('data-variant-secondary-media');
        
        if (variantMedia) {
          try {
            const mediaData = JSON.parse(variantMedia);
            // Preload gallery-sized images for variant picker
            this.preloadGalleryImage(mediaData);
          } catch (e) {
            console.warn('MARCA | ⚠️ Failed to parse variant media:', e);
          }
        }
        
        if (variantSecondaryMedia) {
          try {
            const secondaryMediaData = JSON.parse(variantSecondaryMedia);
            this.preloadGalleryImage(secondaryMediaData);
          } catch (e) {
            console.warn('MARCA | ⚠️ Failed to parse secondary variant media:', e);
          }
        }
      };

      label.addEventListener('mouseenter', preloadVariantMedia, { passive: true });
      label.addEventListener('focus', preloadVariantMedia, { passive: true });
    });

    // Preload thumbnail variant images (these are small: 48px, 68px, 96px, 136px)
    const thumbnailSwatches = variantPicker.querySelectorAll('.thumbnail-swatch img');
    // console.log(`MARCA | 🎨 Found ${thumbnailSwatches.length} thumbnail swatches`);
    
    thumbnailSwatches.forEach((img, index) => {
      const parent = img.closest('.thumbnail-swatch');
      if (!parent) return;

      // Immediately preload the thumbnail images (they're small and fast)
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      
      if (srcset) {
        this.preloadThumbnailSrcset(srcset);
      } else if (src) {
        this.preloadThumbnailSizes(src);
      }
      
      // Only log first few to avoid console spam
      // if (index < 3) {
      //   console.log(`MARCA | 🖼️ Preloading thumbnail swatch ${index + 1}`);
      // }
    });
  }

  /**
   * Preload variant images for a specific product
   */
  async preloadProductVariantImages(productHandle, triggerElement) {
    // Check if we can get variant data from the page
    const productCard = triggerElement.closest('.product-card');
    if (!productCard) return;

    // Look for variant media data in swatches
    const variantInputs = productCard.querySelectorAll('input[data-variant-media]');
    
    // Preload first few variant images (prioritize most common color swatches)
    const maxInitialPreload = 5;
    let preloadCount = 0;
    
    variantInputs.forEach(input => {
      if (preloadCount >= maxInitialPreload) return;
      
      const variantMedia = input.getAttribute('data-variant-media');
      if (variantMedia) {
        try {
          const mediaData = JSON.parse(variantMedia);
          this.preloadGalleryImage(mediaData);
          preloadCount++;
        } catch (e) {
          console.warn('MARCA | ⚠️ Failed to parse variant media:', e);
        }
      }
    });
  }

  /**
   * Preload a variant image from media data
   */
  preloadVariantImage(mediaData) {
    if (!mediaData || !mediaData.src) return;
    
    // Generate optimized image URLs for different sizes
    const imageUrls = this.generateResponsiveImageUrls(mediaData.src);
    
    imageUrls.forEach(url => {
      if (!this.preloadedImages.has(url)) {
        this.scheduleImagePreload(url);
      }
    });
  }

  /**
   * Generate responsive image URLs for preloading
   */
  generateResponsiveImageUrls(baseSrc) {
    const urls = [];
    
    // Remove any existing size parameters
    const cleanSrc = baseSrc.split('?')[0];
    
    // Generate URLs for common sizes (optimized for variant images)
    // Focus on sizes commonly used in the theme
    const sizes = [400, 600, 800, 1000];
    
    sizes.forEach(size => {
      // Shopify image URL format
      if (cleanSrc.includes('cdn.shopify.com')) {
        const url = `${cleanSrc}?width=${size}`;
        urls.push(url);
      }
    });
    
    return urls.length > 0 ? urls : [baseSrc];
  }

  /**
   * Preload larger images from srcset
   */
  preloadLargeImageFromSrcset(srcset) {
    const sources = srcset.split(',');
    
    // Get the largest image from srcset
    const largestSource = sources[sources.length - 1];
    if (!largestSource) return;
    
    const url = largestSource.trim().split(' ')[0];
    if (url && !this.preloadedImages.has(url)) {
      this.scheduleImagePreload(url);
    }
  }

  /**
   * Schedule page preload with delay
   */
  schedulePagePreload(url) {
    if (this.preloadedUrls.has(url)) return;

    // Clear existing timeout for this URL
    if (this.preloadTimeouts[url]) {
      clearTimeout(this.preloadTimeouts[url]);
    }

    // Schedule preload after delay
    this.preloadTimeouts[url] = setTimeout(() => {
      this.preloadPage(url);
      delete this.preloadTimeouts[url];
    }, this.preloadDelay);
  }

  /**
   * Schedule image preload with delay
   */
  scheduleImagePreload(url) {
    if (this.preloadedImages.has(url)) return;

    // Clear existing timeout for this URL
    if (this.preloadTimeouts[url]) {
      clearTimeout(this.preloadTimeouts[url]);
    }

    // Schedule preload after delay (shorter for images)
    this.preloadTimeouts[url] = setTimeout(() => {
      this.preloadImage(url);
      delete this.preloadTimeouts[url];
    }, this.preloadDelay / 2); // Faster for images
  }

  /**
   * Preload a page URL
   */
  async preloadPage(url) {
    if (this.preloadedUrls.has(url)) return;

    // Add to queue if at max concurrent preloads
    if (this.activePreloads >= this.maxConcurrentPreloads) {
      this.preloadQueue.push({ type: 'page', url });
      return;
    }

    this.activePreloads++;

    try {
      // Use link prefetch for page preloading
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      
      document.head.appendChild(link);
      this.preloadedUrls.add(url);
      
      // console.log('MARCA | ✅ Preloaded page:', url);
    } catch (error) {
      console.error('MARCA | ❌ Failed to preload page:', url, error);
    } finally {
      this.activePreloads--;
      this.processQueue();
    }
  }

  /**
   * Preload an image URL
   */
  async preloadImage(url) {
    if (this.preloadedImages.has(url)) return;

    // Add to queue if at max concurrent preloads
    if (this.activePreloads >= this.maxConcurrentPreloads) {
      this.preloadQueue.push({ type: 'image', url });
      return;
    }

    this.activePreloads++;

    try {
      // Use link preload for images (higher priority than prefetch)
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      link.as = 'image';
      
      document.head.appendChild(link);
      this.preloadedImages.add(url);
      
      // Only log first few to avoid console spam
      // if (this.preloadedImages.size <= 10) {
      //   console.log('MARCA | 🖼️ Preloaded image:', url);
      // }
    } catch (error) {
      console.error('MARCA | ❌ Failed to preload image:', url, error);
    } finally {
      this.activePreloads--;
      this.processQueue();
    }
  }

  /**
   * Process the preload queue
   */
  processQueue() {
    if (this.preloadQueue.length === 0) return;
    
    const item = this.preloadQueue.shift();
    
    if (item.type === 'page') {
      this.preloadPage(item.url);
    } else if (item.type === 'image') {
      this.preloadImage(item.url);
    }
  }

  /**
   * Setup scroll-based preloading for products coming into view
   */
  setupScrollPreloading() {
    const productCards = document.querySelectorAll('.product-card');
    if (productCards.length === 0) return;

    // console.log('MARCA | 📜 Setting up scroll-based preloading');

    // Use Intersection Observer for efficient scroll detection
    const observerOptions = {
      root: null,
      rootMargin: '200px', // Start preloading 200px before element enters viewport
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const card = entry.target;
          const productLink = card.querySelector('a[data-instant], a.product-card__media');
          
          if (productLink) {
            const productUrl = productLink.href;
            this.schedulePagePreload(productUrl);
          }
          
          // Stop observing this card after preloading
          observer.unobserve(card);
        }
      });
    }, observerOptions);

    // Observe all product cards
    productCards.forEach(card => {
      observer.observe(card);
    });
  }

  /**
   * Manually preload all variant images on current product page
   */
  preloadAllProductVariants() {
    // console.log('MARCA | 🎨 Preloading all product variants...');
    
    const variantInputs = document.querySelectorAll('input[data-variant-media]');
    
    variantInputs.forEach(input => {
      const variantMedia = input.getAttribute('data-variant-media');
      const variantSecondaryMedia = input.getAttribute('data-variant-secondary-media');
      
      if (variantMedia) {
        try {
          const mediaData = JSON.parse(variantMedia);
          this.preloadGalleryImage(mediaData);
        } catch (e) {
          console.warn('MARCA | ⚠️ Failed to parse variant media:', e);
        }
      }
      
      if (variantSecondaryMedia) {
        try {
          const secondaryMediaData = JSON.parse(variantSecondaryMedia);
          this.preloadGalleryImage(secondaryMediaData);
        } catch (e) {
          console.warn('MARCA | ⚠️ Failed to parse secondary variant media:', e);
        }
      }
    });
  }

  /**
   * Reset preloader state (for testing)
   */
  reset() {
    this.preloadedUrls.clear();
    this.preloadedImages.clear();
    this.preloadQueue = [];
    this.activePreloads = 0;
    
    // Clear all timeouts
    Object.values(this.preloadTimeouts).forEach(timeout => clearTimeout(timeout));
    this.preloadTimeouts = {};
    
    // console.log('MARCA | 🔄 Preloader state reset');
  }
}

// Initialize the preloader when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPreloader);
} else {
  initPreloader();
}

function initPreloader() {
  window.productVariantPreloader = new ProductVariantPreloader();
}

// Optional: Preload all variants on product pages after initial load
window.addEventListener('load', () => {
  if (window.productVariantPreloader && document.querySelector('.product-gallery')) {
    // We're on a product page, preload all variants immediately on iOS, or after short delay on desktop
    const delay = window.productVariantPreloader.isIOS ? 500 : 1500;
    
    setTimeout(() => {
      if (window.productVariantPreloader) {
        // console.log('MARCA | ⚡ Triggering full variant preload...');
        window.productVariantPreloader.preloadAllProductVariants();
      }
    }, delay);
  }
});

// Debug functions (can be called from console)
window.testPreloader = function() {
  if (!window.productVariantPreloader) {
    console.error('MARCA | ❌ Preloader not initialized');
    return;
  }
  
  console.log('MARCA | ✅ Preloader is active');
  console.log('MARCA | 📊 Preloaded URLs:', window.productVariantPreloader.preloadedUrls.size);
  console.log('MARCA | 🖼️ Preloaded images:', window.productVariantPreloader.preloadedImages.size);
  console.log('MARCA | 📱 iOS detected:', window.productVariantPreloader.isIOS);
  console.log('MARCA | 🛒 On product page:', window.productVariantPreloader.isProductPage());
};

window.resetPreloader = function() {
  if (window.productVariantPreloader) {
    window.productVariantPreloader.reset();
  } else {
    console.error('MARCA | ❌ Preloader not initialized');
  }
};

window.preloadAllVariants = function() {
  if (window.productVariantPreloader) {
    window.productVariantPreloader.preloadAllProductVariants();
  } else {
    console.error('MARCA | ❌ Preloader not initialized');
  }
};

// console.log('MARCA | 🔧 Product & Variant Preloader loaded');
// console.log('MARCA | 💡 Debug commands: testPreloader(), resetPreloader(), preloadAllVariants()');

